'use client';
import { useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

export type RealtimeNotifType =
  | 'order_update' |'new_message' |'listing_approved' |'listing_rejected' |'new_inquiry' |'new_match' |'payment_received';

export interface RealtimeNotif {
  id: string;
  user_id: string;
  type: RealtimeNotifType;
  title: string;
  body: string;
  url: string;
  metadata: Record<string, any>;
  is_read: boolean;
  created_at: string;
}

const TYPE_ICONS: Record<RealtimeNotifType, string> = {
  order_update: '📦',
  new_message: '💬',
  listing_approved: '✅',
  listing_rejected: '❌',
  new_inquiry: '📩',
  new_match: '💘',
  payment_received: '💰',
};

async function requestBrowserPermission(): Promise<boolean> {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

function fireBrowserNotification(title: string, body: string, url: string, type: RealtimeNotifType) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  try {
    const notif = new Notification(`${TYPE_ICONS[type] || '🔔'} ${title}`, {
      body,
      icon: '/assets/images/app_logo.png',
      badge: '/favicon.ico',
      tag: `cn-${type}-${Date.now()}`,
      data: { url },
    });
    notif.onclick = () => {
      window.focus();
      if (url && url !== '/') window.location.href = url;
      notif.close();
    };
  } catch {}
}

export interface UseRealtimeNotificationsOptions {
  userId?: string;
  onNotification?: (notif: RealtimeNotif) => void;
  autoRequestPermission?: boolean;
}

export function useRealtimeNotifications({
  userId,
  onNotification,
  autoRequestPermission = true,
}: UseRealtimeNotificationsOptions) {
  const supabase = createClient();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const requestPermission = useCallback(async () => {
    return requestBrowserPermission();
  }, []);

  useEffect(() => {
    if (!userId) return;

    // Auto-request browser notification permission
    if (autoRequestPermission) {
      requestBrowserPermission();
    }

    // Subscribe to realtime_notifications table for this user
    const channel = supabase
      .channel(`realtime-notifs-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'realtime_notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const notif = payload.new as RealtimeNotif;
          // Fire browser push notification
          fireBrowserNotification(notif.title, notif.body, notif.url, notif.type);
          // Call consumer callback
          onNotification?.(notif);
        }
      )
      // Also listen for new messages
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => {
          const msg = payload.new as any;
          fireBrowserNotification(
            'New Message',
            msg.content?.slice(0, 80) || 'You have a new message',
            '/chat',
            'new_message'
          );
          onNotification?.({
            id: msg.id,
            user_id: userId,
            type: 'new_message',
            title: 'New Message',
            body: msg.content?.slice(0, 80) || 'You have a new message',
            url: '/chat',
            metadata: {},
            is_read: false,
            created_at: msg.created_at || new Date().toISOString(),
          });
        }
      )
      // Listen for payment/order updates
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'payment_records',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const rec = payload.new as any;
          const statusLabel = rec.status === 'completed' ? 'completed' : rec.status === 'failed' ? 'failed' : 'updated';
          fireBrowserNotification(
            'Order Update',
            `Your order has been ${statusLabel}`,
            '/bookings',
            'order_update'
          );
          onNotification?.({
            id: rec.id,
            user_id: userId,
            type: 'order_update',
            title: 'Order Update',
            body: `Your order has been ${statusLabel}`,
            url: '/bookings',
            metadata: { order_id: rec.id, status: rec.status },
            is_read: false,
            created_at: rec.updated_at || new Date().toISOString(),
          });
        }
      )
      // Listen for seller_listings status changes (approvals)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'seller_listings',
          filter: `seller_id=eq.${userId}`,
        },
        (payload) => {
          const listing = payload.new as any;
          const old = payload.old as any;
          if (listing.status !== old?.status) {
            const isApproved = listing.status === 'active';
            const type: RealtimeNotifType = isApproved ? 'listing_approved' : 'listing_rejected';
            const title = isApproved ? 'Listing Approved!' : 'Listing Update';
            const body = isApproved
              ? `"${listing.title}" is now live`
              : `"${listing.title}" status: ${listing.status}`;
            fireBrowserNotification(title, body, '/seller-dashboard', type);
            onNotification?.({
              id: listing.id,
              user_id: userId,
              type,
              title,
              body,
              url: '/seller-dashboard',
              metadata: { listing_id: listing.id, status: listing.status },
              is_read: false,
              created_at: listing.updated_at || new Date().toISOString(),
            });
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [userId]);

  return { requestPermission };
}
